import { useEffect, useRef } from 'react'

/**
 * Visibility-aware interval.
 * - Runs callback immediately on mount if runOnInit is true.
 * - Uses `visibleIntervalMs` when the document is visible.
 * - Uses `hiddenIntervalMs` when the document is hidden.
 * - Pauses when `enabled` is false.
 */
export function useVisibilityInterval(
  callback: () => void | Promise<void>,
  options: {
    visibleIntervalMs: number
    hiddenIntervalMs: number
    enabled?: boolean
    runOnInit?: boolean
  }
) {
  const { visibleIntervalMs, hiddenIntervalMs, enabled = true, runOnInit = true } = options
  const intervalIdRef = useRef<number | null>(null)
  const currentCbRef = useRef(callback)

  // Keep latest callback
  useEffect(() => {
    currentCbRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!enabled) {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
      return
    }

    const getIntervalMs = () => (document.visibilityState === 'hidden' ? hiddenIntervalMs : visibleIntervalMs)

    const run = () => {
      try {
        currentCbRef.current()
      } catch {
        // swallow
      }
    }

    // initial run
    if (runOnInit) run()

    // start interval according to visibility
    const start = () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current)
      const ms = getIntervalMs()
      intervalIdRef.current = setInterval(run, ms)
    }

    start()

    const handleVisibility = () => {
      // restart with new cadence
      start()
      // if the page just became visible, trigger an immediate run
      if (document.visibilityState === 'visible') {
        run()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
    }
  }, [visibleIntervalMs, hiddenIntervalMs, enabled, runOnInit])
}


